import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { directory, orgchart } from '@cwos/api-client';
import { Button, ConsequenceNote, Pill, SkeletonSet, SuccessEmpty } from '@cwos/ui-admin';
import { mayListWorkers, usePermissions } from '../permissions.js';
import { authorityGate, gateOpen, gateSentence } from '../authorityGate.js';
import { listPhase } from '../listState.js';
import { pickerState } from '../pickerState.js';
import { personDirectory, type PersonDirectory } from '../personDirectory.js';
import { PersonChip } from '../PersonChip.js';
import { LoadFailure } from '../LoadFailure.js';
import { writeRefusalMessage } from '../ceilingRefusal.js';
import {
  COVERAGE_STANDING_LABEL,
  coverageStanding,
  coverageStandingSentence,
  coverageStandingVariant,
  coverageVerdict,
  coverageVerdictSentence,
  formatWhen,
  fromLocalInputValue,
  toLocalInputValue,
  type CoverageVerdict,
} from './coverageWindows.js';

/**
 * DELEGATION AND COVERAGE — the mechanism by which authority moves temporarily,
 * and until now the one with no screen.
 *
 * ===========================================================================
 * WHAT THIS SURFACE IS FOR
 * ===========================================================================
 * Somebody is away; somebody covers. That sentence is a grant of another
 * person's authority to a third party for a bounded time, and it has been
 * administrable only over raw HTTP. An authority-moving mechanism nobody can
 * see is one nobody can audit — which is the entire reason this screen exists
 * and why every control on it states what is being granted, to whom, and until
 * when BEFORE the press rather than after.
 *
 * ===========================================================================
 * EVALUATE IS NOT A FREE READ
 * ===========================================================================
 * `POST /orgchart/coverage/evaluate` RAISES AN ALERT BY DEFAULT. Its service
 * reads `if (!covered && responsibilities.directReports > 0 && input.raiseTask
 * !== false)` and creates a `management_coverage_gap` alert assigned to a
 * superuser. A screen that evaluated on every keystroke, or on mount, would
 * manufacture alerts about absences nobody has declared.
 *
 * So every exploratory evaluation here passes `raiseTask: false`, and raising
 * the alert is a SEPARATE, deliberate press with its own consequence note. The
 * two are different acts and the screen treats them as different acts.
 *
 * ===========================================================================
 * "COVERED" IS ABOUT A WINDOW, NOT ABOUT NOW
 * ===========================================================================
 * See `coverageWindows.ts` for the full argument. In short: the server's
 * `covered` boolean is `coverage.length > 0` over rows OVERLAPPING the window
 * the caller asked about. A five-day absence with a one-day delegate is
 * `covered: true` with four uncovered days and no alert. An entirely past
 * window is `covered: true` with nothing in force. This screen never renders
 * the bare word; it renders the window, the standing of each record against the
 * clock, and the parts of the window nobody holds.
 *
 * ===========================================================================
 * WHAT IS LENT IS THE SERVER'S ANSWER, TWICE
 * ===========================================================================
 * Before the press, the panel shows what the covered person holds according to
 * `GET /orgchart/users/:id/effective-authority` — the server's own resolution,
 * stamped with the instant it resolved at — and says plainly that the ceiling
 * re-reads it at assignment time, so this is a preview of a live figure rather
 * than the grant.
 *
 * After the press, it shows `inheritedKeys` from the 201, which is what the
 * server actually lent. Nothing on this screen computes an authority: the
 * client cannot see `role_permission`, cannot run `privilegeCeiling`, and a
 * client-side reconstruction of either would be a second answer that drifts.
 */
export function DelegationCoverage() {
  const permissions = usePermissions();
  const qc = useQueryClient();

  // `orgchart:delegate` is what the absence, evaluate, assign and revoke routes
  // all charge. Read routes charge `orgchart:read`, which the /org-chart route
  // guard has already established for anyone who reached this screen.
  const delegateGate = authorityGate(permissions, 'orgchart:delegate');
  const mayDelegate = gateOpen(delegateGate);

  const canReadDirectory = mayListWorkers(permissions);
  const workers = useQuery({
    queryKey: ['directory', 'workers', 'for-orgchart'],
    queryFn: () => directory.workers(),
    enabled: canReadDirectory,
  });
  const people = personDirectory(
    { data: workers.data, isLoading: workers.isLoading, isError: workers.isError },
    canReadDirectory,
  );
  const candidates = (workers.data ?? []).filter((w) => w.userId);

  const [managerUserId, setManagerUserId] = useState('');
  const [from, setFrom] = useState(() => toLocalInputValue(new Date()));
  const [to, setTo] = useState(() => toLocalInputValue(new Date(Date.now() + 7 * 24 * 3600_000)));
  const [assessment, setAssessment] = useState<orgchart.CoverageNeedView | null>(null);
  const [assessedAt, setAssessedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [delegateUserId, setDelegateUserId] = useState('');
  const [reason, setReason] = useState('');
  const [absenceId, setAbsenceId] = useState<string | null>(null);
  const [assigned, setAssigned] = useState<{ id: string; inheritedKeys: string[] } | null>(null);

  /**
   * What the covered manager holds RIGHT NOW, as the server resolves it.
   *
   * `staleTime: 0` / `gcTime: 0` for the same reason the reporting line uses
   * them: this is a statement about an instant, and a cache that outlives the
   * instant turns a live answer into a stale claim about somebody's authority.
   */
  const coveredAuthority = useQuery({
    queryKey: ['orgchart', 'effective-authority', managerUserId],
    queryFn: () => orgchart.effectiveAuthority(managerUserId),
    enabled: managerUserId !== '',
    staleTime: 0,
    gcTime: 0,
  });

  /** The delegate's authority AFTER an assignment — the server's confirmation. */
  const delegateAuthority = useQuery({
    queryKey: ['orgchart', 'effective-authority', delegateUserId, assigned?.id ?? 'none'],
    queryFn: () => orgchart.effectiveAuthority(delegateUserId),
    enabled: assigned !== null && delegateUserId !== '',
    staleTime: 0,
    gcTime: 0,
  });

  const evaluate = useMutation({
    // `raiseTask: false` — see the docblock. This is a question, not a report.
    mutationFn: (raiseTask: boolean) =>
      orgchart.evaluateCoverage({
        managerUserId,
        from: fromLocalInputValue(from) ?? '',
        to: fromLocalInputValue(to) ?? '',
        raiseTask,
      }),
    onSuccess: (result) => {
      setAssessment(result);
      setAssessedAt(new Date());
      setError(null);
    },
    onError: (err: unknown) =>
      setError(writeRefusalMessage(err, 'We couldn’t check coverage for that window.')),
  });

  const declare = useMutation({
    mutationFn: () =>
      orgchart.recordAbsence({
        managerUserId,
        source: 'declared',
        startsAt: fromLocalInputValue(from) ?? '',
        endsAt: fromLocalInputValue(to) ?? '',
        note: reason.trim() === '' ? null : reason.trim(),
      }),
    onSuccess: (absence) => {
      setAbsenceId(absence.id);
      setError(null);
    },
    onError: (err: unknown) =>
      setError(writeRefusalMessage(err, 'We couldn’t record that absence.')),
  });

  const assign = useMutation({
    mutationFn: () =>
      orgchart.assignCoverage({
        coveredUserId: managerUserId,
        delegateUserId,
        // Binds this coverage to the declared absence, so the authority history
        // records the absence playbook rather than an unexplained handover.
        absenceId,
        startsAt: fromLocalInputValue(from) ?? '',
        endsAt: fromLocalInputValue(to) ?? '',
        reason: reason.trim() === '' ? undefined : reason.trim(),
      }),
    onSuccess: (coverage) => {
      setAssigned(coverage);
      setError(null);
      void qc.invalidateQueries({ queryKey: ['orgchart'] });
      // Re-ask, rather than patch the assessment we already have: the server is
      // the only thing that knows what the ceiling let through.
      evaluate.mutate(false);
    },
    onError: (err: unknown) =>
      setError(writeRefusalMessage(err, 'We couldn’t assign that coverage.')),
  });

  const revoke = useMutation({
    mutationFn: (coverageId: string) =>
      orgchart.revokeCoverage(coverageId, 'Revoked from the org chart screen'),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['orgchart'] });
      evaluate.mutate(false);
    },
    onError: (err: unknown) =>
      setError(writeRefusalMessage(err, 'We couldn’t revoke that coverage.')),
  });

  const rosterWithheld = !permissions.isLoading && !permissions.isError && !canReadDirectory;
  const picker = pickerState(
    rosterWithheld
      ? { kind: 'refused' }
      : listPhase(
          permissions.isLoading || permissions.isError
            ? { isLoading: true, isError: false, error: null, data: undefined }
            : workers,
          candidates.length,
        ),
    'people',
    'Choose someone…',
  );

  const windowValid =
    fromLocalInputValue(from) !== null &&
    fromLocalInputValue(to) !== null &&
    new Date(to).getTime() > new Date(from).getTime();

  const verdict: CoverageVerdict | null =
    assessment && assessedAt ? coverageVerdict(assessment, assessedAt) : null;

  const gateNote = gateSentence(delegateGate, 'move authority around in this organization');

  return (
    <div className="rn-col">
      <header className="rn-col">
        <h2>Delegation and coverage</h2>
        <p className="rn-muted">
          When somebody is away, their authority can be lent to somebody else for a bounded time.
          Nothing here is written down as a permanent grant: the server re-resolves coverage from
          its start and end on every request, so a window that has closed confers nothing and nobody
          has to remember to take it back.
        </p>
      </header>

      {/* THE GATE, four-way. `unresolved` and `unknown` are not refusals and
          must not be worded as ones — see authorityGate.ts. */}
      {gateNote ? (
        <p className="rn-muted" role="status" data-delegate-gate={delegateGate.kind}>
          {gateNote}
        </p>
      ) : null}

      <section className="rn-card rn-col" aria-label="The absence">
        <h3>Who is away, and when</h3>
        <label>
          Manager
          <select
            value={managerUserId}
            disabled={picker.disabled}
            onChange={(e) => {
              setManagerUserId(e.target.value);
              setAssessment(null);
              setAssessedAt(null);
              setAssigned(null);
              setAbsenceId(null);
            }}
          >
            <option value="">{picker.placeholder}</option>
            {candidates.map((w) => (
              <option key={w.userId!} value={w.userId!}>
                {w.displayName}
              </option>
            ))}
          </select>
        </label>
        {picker.note ? (
          <p className="rn-muted" role="status">
            {picker.note}
          </p>
        ) : null}

        <div className="rn-row">
          <label>
            From
            <input
              type="datetime-local"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setAssessment(null);
              }}
            />
          </label>
          <label>
            Until
            <input
              type="datetime-local"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setAssessment(null);
              }}
            />
          </label>
        </div>
        {windowValid ? null : (
          <p className="rn-muted" role="status">
            A coverage window has to end after it starts. Coverage that cannot end is not coverage —
            the server refuses it, and so does this form.
          </p>
        )}

        <div className="rn-row">
          <Button
            variant="secondary"
            disabled={!mayDelegate || managerUserId === '' || !windowValid || evaluate.isPending}
            onClick={() => evaluate.mutate(false)}
          >
            {evaluate.isPending ? 'Checking…' : 'Check coverage for this window'}
          </Button>
          <Button
            variant="secondary"
            disabled={!mayDelegate || managerUserId === '' || !windowValid || declare.isPending}
            onClick={() => declare.mutate()}
          >
            {declare.isPending ? 'Recording…' : 'Record this absence'}
          </Button>
        </div>
        <ConsequenceNote text="Checking asks a question and changes nothing. Recording the absence stores it, so a handover assigned afterwards is recorded in the authority history as planned leave rather than as somebody lending their authority away for no stated reason." />
        {absenceId ? (
          <p className="rn-muted" role="status" data-absence-recorded={absenceId}>
            Absence recorded. Coverage assigned below will be linked to it.
          </p>
        ) : null}
      </section>

      {error ? (
        <p className="rn-card" role="alert" data-delegation-error="true">
          {error}
        </p>
      ) : null}

      {verdict && assessment ? (
        <CoverageAssessment
          assessment={assessment}
          verdict={verdict}
          people={people}
          now={assessedAt!}
          mayDelegate={mayDelegate}
          onRaiseAlert={() => evaluate.mutate(true)}
          raising={evaluate.isPending}
          onRevoke={(id) => revoke.mutate(id)}
          revoking={revoke.isPending}
        />
      ) : null}

      {managerUserId === '' ? null : (
        <section className="rn-card rn-col" aria-label="Assign coverage">
          <h3>Lend this authority to somebody</h3>

          <WhatWouldBeLent query={coveredAuthority} coveredName={people.name(managerUserId)} />

          <label>
            Delegate
            <select
              value={delegateUserId}
              disabled={picker.disabled}
              onChange={(e) => {
                setDelegateUserId(e.target.value);
                setAssigned(null);
              }}
            >
              <option value="">{picker.placeholder}</option>
              {candidates
                .filter((w) => w.userId !== managerUserId)
                .map((w) => (
                  <option key={w.userId!} value={w.userId!}>
                    {w.displayName}
                  </option>
                ))}
            </select>
          </label>

          <label>
            Why (recorded in the authority history)
            <input
              type="text"
              value={reason}
              maxLength={500}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Planned leave, on-call handover…"
            />
          </label>

          {/* WHAT IS BEING GRANTED, TO WHOM, UNTIL WHEN — said before the press. */}
          <ConsequenceNote
            text={
              delegateUserId === '' || !windowValid
                ? 'Choose a delegate and a valid window. The sentence describing exactly what this grants will appear here before you can press anything.'
                : `This lends ${people.name(managerUserId)}’s authority to ${people.name(delegateUserId)} from ${formatWhen(fromLocalInputValue(from) ?? from)} until ${formatWhen(fromLocalInputValue(to) ?? to)}, and no longer. The server caps what is lent at what ${people.name(managerUserId)} holds at the moment the ceiling runs, and caps it again at what you yourself hold — you cannot lend authority you do not have. Nothing is written down as a grant; it stops on its own at the end of the window.`
            }
          />

          <div className="rn-row">
            <Button
              variant="primary"
              disabled={
                !mayDelegate ||
                delegateUserId === '' ||
                managerUserId === '' ||
                !windowValid ||
                assign.isPending
              }
              onClick={() => assign.mutate()}
            >
              {assign.isPending ? 'Assigning…' : 'Assign this coverage'}
            </Button>
          </div>

          {assigned ? (
            <AssignmentOutcome
              assigned={assigned}
              delegateName={people.name(delegateUserId)}
              coveredName={people.name(managerUserId)}
              window={{
                startsAt: fromLocalInputValue(from) ?? from,
                endsAt: fromLocalInputValue(to) ?? to,
              }}
              authority={delegateAuthority}
            />
          ) : null}
        </section>
      )}
    </div>
  );
}

/**
 * The assessment, told apart from its own boolean.
 *
 * Every number on this panel is labelled with the window it is about. The
 * heading deliberately does NOT read "Covered" or "Not covered".
 */
function CoverageAssessment({
  assessment,
  verdict,
  people,
  now,
  mayDelegate,
  onRaiseAlert,
  raising,
  onRevoke,
  revoking,
}: {
  assessment: orgchart.CoverageNeedView;
  verdict: CoverageVerdict;
  people: PersonDirectory;
  now: Date;
  mayDelegate: boolean;
  onRaiseAlert: () => void;
  raising: boolean;
  onRevoke: (coverageId: string) => void;
  revoking: boolean;
}) {
  const managerName = people.name(assessment.managerUserId);
  const r = assessment.responsibilities;

  return (
    <section className="rn-card rn-col" aria-label="Coverage assessment">
      <h3>What happens to {managerName}’s work in that window</h3>

      <p
        data-coverage-overlapping={verdict.overlapping}
        data-coverage-in-force={verdict.inForceNow}
      >
        {coverageVerdictSentence(verdict, managerName)}
      </p>

      <p className="rn-muted">
        {r.directReports === 0
          ? `${managerName} has no authority-bearing reporting relationships on the day this window opens, so there is nothing for a delegate to pick up. That is a real answer, not an empty list.`
          : `${r.directReports} reporting relationship${r.directReports === 1 ? '' : 's'} route${r.directReports === 1 ? 's' : ''} work through ${managerName}${scopeTail(r)}.`}
      </p>

      {verdict.gaps.length > 0 ? (
        <div className="rn-col" data-coverage-gaps={verdict.gaps.length}>
          <h4>Uncovered parts of the window</h4>
          <ul className="rn-list">
            {verdict.gaps.map((gap) => (
              <li key={`${gap.from}-${gap.to}`}>
                {formatWhen(gap.from)} → {formatWhen(gap.to)}
              </li>
            ))}
          </ul>
          <p className="rn-muted">
            A record touching the window is not the same as the window being held. These stretches
            have nobody, and the platform raises no alert about them on its own once any coverage
            exists.
          </p>
        </div>
      ) : null}

      <h4>Coverage records touching this window</h4>
      {assessment.coverage.length === 0 ? (
        <SuccessEmpty
          heading="No coverage records"
          message={`Nothing has been arranged for ${managerName} across any part of ${formatWhen(verdict.window.from)} → ${formatWhen(verdict.window.to)}. This is a measured answer about that window — the server was asked and returned none.`}
        />
      ) : (
        <ul className="rn-list">
          {assessment.coverage.map((row) => {
            const standing = coverageStanding(row, now);
            return (
              <li
                key={row.id}
                data-coverage-id={row.id}
                data-coverage-standing={standing}
                data-coverage-ends={row.endsAt}
              >
                <PersonChip person={people.person(row.delegateUserId)} />
                <Pill variant={coverageStandingVariant(standing)}>
                  {COVERAGE_STANDING_LABEL[standing]}
                </Pill>
                <p>{coverageStandingSentence(row, people.name(row.delegateUserId), now)}</p>
                {/* An expired window has nothing left to revoke — offering the
                    control would imply authority is still being conferred. */}
                {standing === 'expired' ? (
                  <p className="rn-muted">
                    Nothing to revoke: this window closed by itself and the server stopped resolving
                    it at that instant.
                  </p>
                ) : (
                  <Button
                    variant="destructive"
                    small
                    disabled={!mayDelegate || revoking}
                    onClick={() => onRevoke(row.id)}
                  >
                    End this coverage now
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <p className="rn-muted">
        Resolved at {now.toLocaleTimeString()} against the window the server echoed back (
        {formatWhen(verdict.window.from)} → {formatWhen(verdict.window.to)}). Standing is measured
        against the clock at that moment, not against how recently a record was created.
      </p>

      {verdict.gaps.length > 0 && r.directReports > 0 ? (
        <div className="rn-col">
          <ConsequenceNote text="Raising the alert creates a coverage-gap task assigned to a superuser, visible in the alert queue. Checking coverage above never does this — an alert should exist because somebody decided it should, not because a screen was opened." />
          <Button variant="secondary" disabled={!mayDelegate || raising} onClick={onRaiseAlert}>
            {raising ? 'Raising…' : 'Raise a coverage-gap alert'}
          </Button>
        </div>
      ) : null}

      {assessment.taskAlertId ? (
        <p className="rn-muted" role="status" data-coverage-alert={assessment.taskAlertId}>
          A coverage-gap alert was raised for this window.
        </p>
      ) : null}
    </section>
  );
}

/**
 * WHAT WOULD BE LENT — the server's answer, with its instant, and never a
 * client-side reconstruction.
 *
 * The four states are all here and all different: unresolved (asking),
 * unreachable (failed — say so, do not say "nothing"), refused (the server said
 * no — no retry), and a real, measured answer including a real zero.
 */
function WhatWouldBeLent({
  query,
  coveredName,
}: {
  query: {
    data: orgchart.EffectiveAuthorityView | undefined;
    isLoading: boolean;
    isError: boolean;
    error: unknown;
    refetch: () => void;
  };
  coveredName: string;
}) {
  if (query.isLoading) return <SkeletonSet surface="drawer" rows={2} />;
  if (query.isError) {
    return (
      <LoadFailure
        error={query.error}
        message={`We couldn’t read what ${coveredName} currently holds. Do not read this as “they hold nothing” — it is a failure to ask, not an answer.`}
        onRetry={() => query.refetch()}
      />
    );
  }
  const authority = query.data;
  if (!authority) return null;

  return (
    <div className="rn-col" data-would-lend-count={authority.own.length}>
      <h4>What {coveredName} holds right now</h4>
      {authority.own.length === 0 ? (
        <SuccessEmpty
          heading="No grants"
          message={`${coveredName} holds no permission grants at this instant, so a delegate would inherit nothing from them. That is a measured zero — the server resolved it and returned an empty set.`}
        />
      ) : (
        <p>
          {authority.own.length} permission grant{authority.own.length === 1 ? '' : 's'}, resolved
          by the server at {new Date(authority.at).toLocaleTimeString()}.
        </p>
      )}
      <p className="rn-muted">
        This is a preview of a live figure, not the grant. The ceiling re-reads what {coveredName}{' '}
        holds at the moment you assign, and caps the result again at what you hold yourself — so
        what is actually lent is stated by the server after the press, not calculated here.
      </p>
    </div>
  );
}

/**
 * What the server said it lent — and, crucially, whether anything has moved yet.
 *
 * A future window is the interesting case: the coverage row exists, the grant
 * is recorded, and `effectiveAuthority(delegate).delegated` is EMPTY because
 * `starts_at <= now() < ends_at` is not yet true. Rendering that as "the
 * delegation failed" would be wrong and rendering it as "they now hold it"
 * would be worse. It is neither: it is scheduled.
 */
function AssignmentOutcome({
  assigned,
  delegateName,
  coveredName,
  window,
  authority,
}: {
  assigned: { id: string; inheritedKeys: string[] };
  delegateName: string;
  coveredName: string;
  window: { startsAt: string; endsAt: string };
  authority: {
    data: orgchart.EffectiveAuthorityView | undefined;
    isLoading: boolean;
    isError: boolean;
    error: unknown;
    refetch: () => void;
  };
}) {
  const now = new Date();
  const standing = coverageStanding(window, now);
  const held = authority.data?.delegated.filter((d) => d.coverageId === assigned.id) ?? [];

  return (
    <div className="rn-col" role="status" data-assigned-coverage={assigned.id}>
      <h4>Assigned</h4>
      <p data-inherited-keys={assigned.inheritedKeys.length}>
        The server lent {assigned.inheritedKeys.length} permission
        {assigned.inheritedKeys.length === 1 ? '' : 's'} of {coveredName}’s to {delegateName}, from{' '}
        {formatWhen(window.startsAt)} until {formatWhen(window.endsAt)}. This count is the server’s
        own answer after the ceiling ran, not the preview shown before the press.
      </p>
      {assigned.inheritedKeys.length > 0 ? (
        <ul className="rn-list">
          {assigned.inheritedKeys.map((key) => (
            <li key={key}>
              <code>{key}</code>
            </li>
          ))}
        </ul>
      ) : (
        <p className="rn-muted">
          The ceiling let nothing through. The coverage record exists and confers nothing — usually
          because {coveredName} holds no grants, or because every one of theirs is outside what you
          may confer.
        </p>
      )}

      {authority.isError ? (
        <LoadFailure
          error={authority.error}
          message={`We couldn’t re-read what ${delegateName} holds. The coverage was assigned; this is only the confirmation read failing.`}
          onRetry={() => authority.refetch()}
        />
      ) : standing === 'scheduled' ? (
        <p className="rn-muted" data-delegation-live="scheduled">
          Nothing has moved yet. The window opens at {formatWhen(window.startsAt)}, and until then
          the server resolves {delegateName}’s delegated authority as empty — which is what it is
          reporting now, correctly.
        </p>
      ) : authority.isLoading ? (
        <SkeletonSet surface="drawer" rows={1} />
      ) : held.length > 0 ? (
        <p className="rn-muted" data-delegation-live="in_force">
          The server now resolves {delegateName} as holding {held[0]!.grants.length} borrowed grant
          {held[0]!.grants.length === 1 ? '' : 's'} under this coverage, until{' '}
          {formatWhen(held[0]!.endsAt)}.
        </p>
      ) : (
        <p className="rn-muted" data-delegation-live="absent">
          The server does not currently resolve this coverage into any delegated authority for{' '}
          {delegateName}. The record exists; what it confers is decided live, and right now it
          confers nothing.
        </p>
      )}
    </div>
  );
}

function scopeTail(r: orgchart.CoverageNeedView['responsibilities']): string {
  const parts: string[] = [];
  if (r.programs.length > 0) parts.push(`${r.programs.length} program(s)`);
  if (r.locations.length > 0) parts.push(`${r.locations.length} location(s)`);
  if (r.teams.length > 0) parts.push(`${r.teams.length} team(s)`);
  return parts.length === 0 ? '' : `, across ${parts.join(', ')}`;
}
